import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const AudioContext = createContext();

// Sound definitions - Phase 1: Essential sounds only
const SOUNDS = {
  // Essential
  diceRoll: '/sounds/essential/dice-roll.mp3',
  markNumber: '/sounds/essential/mark-number.mp3',
  turnChange: '/sounds/essential/turn-change.mp3',
  timerTick: '/sounds/essential/timer-tick.mp3',
};

// Category mapping
const SOUND_CATEGORIES = {
  diceRoll: 'essential',
  markNumber: 'essential',
  turnChange: 'essential',
  timerTick: 'essential',
};

const DEFAULT_SETTINGS = {
  masterVolume: 0.7,
  muted: false,
  categories: {
    essential: true,
  },
  hapticEnabled: true,
};

export const AudioProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('qwixx_audio_settings');
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const audioRefs = useRef({});
  const audioUnlocked = useRef(false);
  const webAudioContextRef = useRef(null);

  // Preload all sounds
  useEffect(() => {
    Object.entries(SOUNDS).forEach(([key, src]) => {
      try {
        const audio = new Audio(src);
        audio.preload = 'auto';
        audio.volume = settings.masterVolume;
        audioRefs.current[key] = audio;
      } catch (error) {
        console.warn(`Failed to load sound: ${key}`, error);
      }
    });
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('qwixx_audio_settings', JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save audio settings', error);
    }
  }, [settings]);

  // Unlock audio on first user interaction (required by browsers)
  const unlockAudio = useCallback(() => {
    if (audioUnlocked.current) return;

    // Use Web Audio API to unlock audio context (no audible sound)
    try {
      if (!webAudioContextRef.current) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          webAudioContextRef.current = new AudioContextClass();
        }
      }

      if (webAudioContextRef.current && webAudioContextRef.current.state === 'suspended') {
        webAudioContextRef.current.resume();
      }

      // Also touch each HTML Audio element to enable it (without playing)
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          // Load the audio without playing
          audio.load();
        }
      });

      audioUnlocked.current = true;
      console.log('Audio unlocked');
    } catch (error) {
      console.warn('Failed to unlock audio:', error);
    }
  }, []);

  // Setup unlock listeners
  useEffect(() => {
    const events = ['touchstart', 'touchend', 'mousedown', 'keydown', 'click'];

    const handleInteraction = () => {
      unlockAudio();
      // Remove listeners after first interaction
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };

    events.forEach(event => {
      document.addEventListener(event, handleInteraction, { once: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, [unlockAudio]);

  // Play sound function
  const playSound = useCallback((soundName) => {
    if (settings.muted) return;

    const category = SOUND_CATEGORIES[soundName];
    const audio = audioRefs.current[soundName];

    if (!audio) {
      console.warn(`Sound not found: ${soundName}`);
      return;
    }
    if (category && !settings.categories[category]) return;

    try {
      audio.volume = settings.masterVolume;
      audio.currentTime = 0;
      audio.play().catch((error) => {
        // Ignore autoplay errors - they're expected before user interaction
        if (error.name !== 'NotAllowedError') {
          console.warn(`Failed to play sound: ${soundName}`, error);
        }
      });
    } catch (error) {
      console.warn(`Error playing sound: ${soundName}`, error);
    }
  }, [settings]);

  // Stop sound function
  const stopSound = useCallback((soundName) => {
    const audio = audioRefs.current[soundName];
    if (!audio) return;

    try {
      audio.pause();
      audio.currentTime = 0;
    } catch (error) {
      // Ignore errors
    }
  }, []);

  // Haptic feedback
  const vibrate = useCallback((pattern = 50) => {
    if (!settings.hapticEnabled) return;
    if (!navigator.vibrate) return;

    try {
      navigator.vibrate(pattern);
    } catch (error) {
      // Vibration not supported or blocked
    }
  }, [settings.hapticEnabled]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    setSettings(prev => ({ ...prev, muted: !prev.muted }));
  }, []);

  // Update master volume
  const setMasterVolume = useCallback((volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    setSettings(prev => ({ ...prev, masterVolume: clampedVolume }));

    // Update volume on all loaded audio elements
    Object.values(audioRefs.current).forEach(audio => {
      if (audio) audio.volume = clampedVolume;
    });
  }, []);

  // Update settings
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  // Toggle category
  const toggleCategory = useCallback((category) => {
    setSettings(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: !prev.categories[category]
      }
    }));
  }, []);

  // Toggle haptic
  const toggleHaptic = useCallback(() => {
    setSettings(prev => ({ ...prev, hapticEnabled: !prev.hapticEnabled }));
  }, []);

  return (
    <AudioContext.Provider value={{
      settings,
      playSound,
      stopSound,
      vibrate,
      toggleMute,
      setMasterVolume,
      updateSettings,
      toggleCategory,
      toggleHaptic,
      unlockAudio,
    }}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export default AudioContext;
